
import React, { useState, useEffect } from "react";
import { Modal, Button, Spinner } from "react-bootstrap";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const MarkSheetModal = ({ show, onHide, student }) => {
    const [isDownloading, setIsDownloading] = useState(false);
    const [marksheetData, setMarksheetData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const LMS = process.env.REACT_APP_API_LMS;
    const token = process.env.REACT_APP_API_TOKEN;

    useEffect(() => {
        if (show && student?.id) {
            fetchMarksheetData();
        }
    }, [show, student?.id]);

    const fetchMarksheetData = async () => {
        try {
            setIsLoading(true);
            setError(null);
            
            const response = await fetch(`${LMS}/api/batch/student/marksheet`, {
                method: "POST",
                headers: {
                    "Authorization": token,
                    "Accept": "application/json",
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({
                    student_id: student.id,
                    course_ids:[134, 136, 137, 141, 142,144,145,149]
                })
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();

            if (data.status) {
                setMarksheetData(data.data);
            } else {
                setError(data.message || "Failed to fetch marksheet data");
            }
        } catch (err) {
            console.error("Error fetching marksheet data:", err);
            setError("Error fetching marksheet data. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    // Helper function to get totals from data
    const getTotals = () => {
        if (!marksheetData?.totals) {
            return {
                theoryTotal: 0,
                assignmentTotal: 0,
                internshipTotal: 0,
                grandTotal: 0,
                percentage: 0,
                resultClass: "-"
            };
        }

        const t = marksheetData.totals;
        return {
            theoryTotal: t.theory_total || 0,
            assignmentTotal: t.assignment_total || 0,
            internshipTotal: t.internship_total || 0,
            grandTotal: t.grand_total || 0,
            percentage: t.percentage || 0,
            resultClass: t.classification || "-"
        };
    };

    const totals = getTotals();

    const handleDownloadPDF = async () => {
        const container = document.querySelector(".marksheet-container");
        const element = document.querySelector(".marksheet");
        if (!container || !element) return;

        let wrapper = null;
        try {
            setIsDownloading(true);

            // Create a hidden wrapper inside the container to preserve all CSS inheritance/fonts
            wrapper = document.createElement("div");
            Object.assign(wrapper.style, {
                position: "absolute",
                top: "0",
                left: "0",
                width: "0",
                height: "0",
                overflow: "hidden",
                zIndex: "-9999",
                pointerEvents: "none"
            });

            // Clone the marksheet element
            const clone = element.cloneNode(true);
            Object.assign(clone.style, {
                width: "788px", // Match exact min-width/desktop layout width
                minWidth: "788px",
                maxWidth: "788px",
                height: "auto",
                margin: "0",
                padding: "10px",
                boxSizing: "border-box",
                backgroundColor: "#ffffff",
                position: "relative"
            });

            wrapper.appendChild(clone);
            container.appendChild(wrapper);

            // Capture the clone using html2canvas
            const canvas = await html2canvas(clone, {
                scale: 3, // Higher scale for better quality
                useCORS: true,
                logging: false,
                backgroundColor: '#ffffff',
                windowWidth: 788
            });

            // Clean up the wrapper
            container.removeChild(wrapper);
            wrapper = null;

            const imgData = canvas.toDataURL("image/png");

            // Create PDF (A4 portrait)
            const pdf = new jsPDF({
                orientation: "portrait",
                unit: "mm",
                format: "a4",
            });

            const imgWidth = 210; // A4 width in mm
            const pageHeight = 297; // A4 height in mm

            // Calculate height while maintaining aspect ratio
            const imgHeight = (canvas.height * imgWidth) / canvas.width;

            let heightLeft = imgHeight;
            let position = 0;

            pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
            heightLeft -= pageHeight;

            // Handle multi-page content
            while (heightLeft >= 0) {
                position = heightLeft - imgHeight;
                pdf.addPage();
                pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
                heightLeft -= pageHeight;
            }

            const studentName = (marksheetData?.personal_info?.student_name || student?.name || "Student").replace(/\s+/g, '_');
            const dateStr = new Date().toISOString().split('T')[0];
            const fileName = `Marksheet_${studentName}_${dateStr}.pdf`;
            pdf.save(fileName);
        } catch (error) {
            console.error("Error generating PDF:", error);
            alert("Failed to generate PDF. Please try again.");
        } finally {
            if (wrapper && container.contains(wrapper)) {
                container.removeChild(wrapper);
            }
            setIsDownloading(false);
        }
    };

    return (
        <Modal show={show} onHide={onHide} size="lg" centered dialogClassName="modal-90w">
            <Modal.Header closeButton>
                <Modal.Title>Student Marksheet</Modal.Title>
            </Modal.Header>
            <Modal.Body className="p-0">
                <div className="d-flex justify-content-end p-3 bg-light border-bottom">
                    <Button
                        variant="success"
                        onClick={handleDownloadPDF}
                        disabled={isDownloading || isLoading || !!error}
                    >
                        {isDownloading ? 'Generating PDF...' : 'Download PDF'}
                    </Button>
                </div>
                <div className="marksheet-container">
                    {isLoading ? (
                        <div className="d-flex justify-content-center align-items-center" style={{ height: "400px" }}>
                            <div className="text-center">
                                <Spinner animation="border" variant="primary" />
                                <p className="mt-2">Fetching Marksheet Data...</p>
                            </div>
                        </div>
                    ) : error ? (
                        <div className="d-flex justify-content-center align-items-center" style={{ height: "400px" }}>
                            <div className="alert alert-danger mx-4" role="alert">
                                {error}
                                <div className="mt-2">
                                    <Button size="sm" variant="outline-danger" onClick={fetchMarksheetData}>Retry</Button>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="marksheet">
                            <div className="watermark">
                                <img src="/assets/images/hunarho_small_logo.png" alt="Watermark" />
                            </div>
                            <div className="header">
                                <div className="header-logo-centered">
                                    <img
                                        src="/assets/images/logo.png"
                                        alt="Logo"
                                    />
                                </div>
                                <div className="header-text-centered">
                                    <h1>EARLY CHILDHOOD CARE AND EDUCATION</h1>
                                    <div className="header-subtitle-badge">
                                        <h2>COURSE COMPLETION MARKSHEET</h2>
                                    </div>
                                </div>
                            </div>
                            <div className="student-info">
                                <div className="info-row">
                                    <strong>Student Name:</strong> {marksheetData?.personal_info?.student_name || student?.name || "Student"}
                                </div>
                                <div className="info-row">
                                    <strong>Enrollment No:</strong> {marksheetData?.personal_info?.enrollment_no || "N/A"}
                                </div>
                                <div className="info-row">
                                    <strong>Course Name:</strong> {marksheetData?.personal_info?.course_name || "ECCED"}
                                </div>
                                <div className="info-row">
                                    <strong>Academic Year:</strong> {marksheetData?.personal_info?.academic_year || "N/A"}
                                </div>
                                <div className="info-row">
                                    <strong>Institute:</strong> {marksheetData?.personal_info?.institute || "Learning Hub Education Center"}
                                </div>
                                <div className="info-row">
                                    <strong>Date of Issue:</strong> {marksheetData?.personal_info?.date_of_issue || new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                                </div>
                            </div>
                            <div className="section-title">Part 1: Theory Assessment</div>
                            <table className="three-column-table">
                                <thead>
                                    <tr>
                                        <th>ECCE Units</th>
                                        <th className="center">Maximum Marks</th>
                                        <th className="center">Marks Obtained</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {marksheetData?.theory_assessment && marksheetData.theory_assessment.length > 0 ? (
                                        marksheetData.theory_assessment.map((item, index) => (
                                            <tr key={index}>
                                                <td>{item.course_name}</td>
                                                <td className="center">{item.total_points || "10"}</td>
                                                <td className="center">{item.course_id === 149 ? (item.achieved_points !== null ? item.achieved_points : "0") : (item.marks_out_of_10 !== null ? item.marks_out_of_10 : "0")}</td>
                                            </tr>
                                        ))
                                    ) : (
                                        <>
                                            <tr>
                                                <td>Child Growth and Development in Early Childhood Unit 1</td>
                                                <td className="center">10</td>
                                                <td className="center">-</td>
                                            </tr>
                                            <tr>
                                                <td>Fostering Health and Learning in Early Childhood Unit 2</td>
                                                <td className="center">10</td>
                                                <td className="center">-</td>
                                            </tr>
                                            <tr>
                                                <td>Curriculum Planning & Pedagogical Strategies Unit 3</td>
                                                <td className="center">10</td>
                                                <td className="center">-</td>
                                            </tr>
                                            <tr>
                                                <td>Management and Instructional Strategies Unit 4</td>
                                                <td className="center">10</td>
                                                <td className="center">-</td>
                                            </tr>
                                            <tr>
                                                <td>Professional skill enhancement Unit 5</td>
                                                <td className="center">10</td>
                                                <td className="center">-</td>
                                            </tr>
                                            <tr>
                                                <td>ECCE Final Exam</td>
                                                <td className="center">50</td>
                                                <td className="center">-</td>
                                            </tr>
                                        </>
                                    )}
                                    <tr className="grand-total">
                                        <td><strong>Total Theory Marks</strong></td>
                                        <td className="center"><strong>100</strong></td>
                                        <td className="center"><strong>{totals.theoryTotal}</strong></td>
                                    </tr>
                                </tbody>
                            </table>
                            <div className="section-title">Part 2: Assignment Marks</div>
                            <table className="assignment-marks-table">
                                <thead>
                                    <tr>
                                        <th>Assignment No.</th>
                                        <th className="center">Marks</th>
                                        <th>Assignment No.</th>
                                        <th className="center">Marks</th>
                                        <th>Assignment No.</th>
                                        <th className="center">Marks</th>
                                        <th>Assignment No.</th>
                                        <th className="center">Marks</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {marksheetData?.assignment_marks && marksheetData.assignment_marks.length > 0 ? (
                                        // Creating vertical-fill rows for the 4-column vertical layout (A1-A5, A6-A10, A11-A15, A16-A20)
                                        Array.from({ length: 5 }).map((_, rowIndex) => (
                                            <tr key={rowIndex}>
                                                {/* Column 1 (A1 - A5) */}
                                                <td>Assignment {rowIndex + 1}</td>
                                                <td className="center">{marksheetData.assignment_marks[rowIndex]?.marks ?? "-"}</td>
                                                
                                                {/* Column 2 (A6 - A10) */}
                                                <td>Assignment {rowIndex + 6}</td>
                                                <td className="center">{marksheetData.assignment_marks[rowIndex + 5]?.marks ?? "-"}</td>
                                                
                                                {/* Column 3 (A11 - A15) */}
                                                <td>Assignment {rowIndex + 11}</td>
                                                <td className="center">{marksheetData.assignment_marks[rowIndex + 10]?.marks ?? "-"}</td>
                                                
                                                {/* Column 4 (A16 - A20) */}
                                                <td>Assignment {rowIndex + 16}</td>
                                                <td className="center">{marksheetData.assignment_marks[rowIndex + 15]?.marks ?? "-"}</td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="8" className="center">No assignment data available</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                            <table className="three-column-table">
                                <tbody>
                                    <tr className="grand-total">
                                        <td><strong>Total Assignment Marks</strong></td>
                                        <td className="center"><strong>80</strong></td>
                                        <td className="center"><strong>{totals.assignmentTotal}</strong></td>
                                    </tr>
                                </tbody>
                            </table>
                            <div className="section-title">Part 3: Internship / Capstone Project</div>
                            <table className="three-column-table">
                                <thead>
                                    <tr>
                                        <th>Internship / Capstone Project</th>
                                        <th className="center">Maximum Marks</th>
                                        <th className="center">Marks Obtained</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {marksheetData?.internship_or_capston_project && marksheetData.internship_or_capston_project.length > 0 ? (
                                        marksheetData.internship_or_capston_project.map((item, index) => (
                                            <tr key={index}>
                                                <td>{item.course_name === "ECCE Internship" ? "Internship" : "Capstone Project"}</td>
                                                <td className="center">{item.maximum_marks || "20"}</td>
                                                <td className="center">{item.marks_obtained !== null ? item.marks_obtained : "-"}</td>
                                            </tr>
                                        ))
                                    ) : (
                                        <>
                                            <tr>
                                                <td>Internship (OR) Capstone Project</td>
                                                <td className="center">20</td>
                                                <td className="center">-</td>
                                            </tr>
                                        </>
                                    )}
                                    {/* <tr className="grand-total">
                                        <td><strong>Total Internship / Capstone Marks</strong></td>
                                        <td className="center"><strong>20</strong></td>
                                        <td className="center"><strong>{totals.internshipTotal}</strong></td>
                                    </tr> */}
                                </tbody>
                            </table>
                            <table className="three-column-table">
                                <tbody>
                                    <tr className="grand-total">
                                        <td><strong>Grand Total Marks</strong></td>
                                        <td className="center"><strong>200</strong></td>
                                        <td className="center"><strong>{totals.grandTotal}</strong></td>
                                    </tr>
                                    <tr className="grand-total">
                                        <td><strong>Total Percentage</strong></td>
                                        <td className="center"><strong>100%</strong></td>
                                        <td className="center"><strong>{typeof totals.percentage === 'number' ? totals.percentage.toFixed(2) : totals.percentage}%</strong></td>
                                    </tr>
                                    <tr className="grand-total">
                                        <td><strong>Result / Class</strong></td>
                                        <td colSpan="2" className="center" style={{ color: totals.percentage >= 50 ? '#297757' : '#DC3545' }}>
                                            <strong>{totals.resultClass}</strong>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                            <div className="classification">
                                <div className="section-title">Classification of Results</div>
                                <table>
                                    <tbody>
                                        <tr>
                                            <td><strong>Pass Class</strong></td>
                                            <td>50%</td>
                                            <td><strong>Second Class</strong></td>
                                            <td>55%</td>
                                            <td><strong>First Class</strong></td>
                                            <td>65%</td>
                                            <td><strong>Distinction</strong></td>
                                            <td>75%</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                            <div className="signatures">
                                <div className="signature-block">
                                    <img 
                                        src={"/assets/images/sign/TS_Signature.png"} 
                                        alt="Evaluator Signature" 
                                        className="signature-image" 
                                    />
                                    <div className="signature-line">Evaluator Signature</div>
                                </div>
                                <div className="signature-block">
                                    <img 
                                        src={"/assets/images/sign/NS_Sign.png"} 
                                        alt="Evaluator Signature" 
                                        className="signature-image-1" 
                                    />
                                    <div className="signature-line">Authorized Signatory</div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
                <style>
                    {`
            .marksheet-container {
              background-color: #f5f5f5;
              padding: 5px 5px;
              overflow-x: auto;
            }
            .marksheet {
              position: relative;
              width: 100%;
              min-width: 788px;
              max-width: 900px;
              margin: 0 auto;
              background-color: white;
              padding: 10px;
              border: 1px solid #CDD4FB;
              font-family: 'Typography', sans-serif;
              z-index: 1;
            }
            .watermark {
              position: absolute;
              top: 50%;
              left: 50%;
              transform: translate(-50%, -50%);
              opacity: 0.1;
              z-index: -1;
              pointer-events: none;
              width: 80%;
              display: flex;
              justify-content: center;
              align-items: center;
            }
            .watermark img {
              width: 100%;
              max-width: 400px;
              object-fit: contain;
            }
            .header {
              text-align: center;
              border-bottom: 4px double #36348E;
              margin-bottom: 5px;
              padding-bottom: 5px;
              display: flex;
              flex-direction: column;
              align-items: center;
            }
            .header-logo-centered img {
              height: 45px;
              object-fit: contain;
            }
            .header-text-centered h1 {
              font-size: 20px;
              font-weight: 700;
              margin: 5px 0;
              color: #36348E;
              letter-spacing: 1px;
              text-transform: uppercase;
            }
            .header-subtitle-badge {
              display: flex;
              justify-content: center;
              align-items: center;
            }
            .header-subtitle-badge h2 {
              display: inline-block;
              background-color: #36348E;
              color: #ffffff;
              font-size: 13px;
              margin: 0;
              padding: 2px 25px;
              border-radius: 4px;
              text-transform: uppercase;
              letter-spacing: 3px;
              position: relative;
              z-index: 5;
            }
            .student-info {
              display: grid;
              grid-template-columns: 1fr 1fr;
              font-size: 13px;
              gap: 5px;
            }
            .info-row {
              padding: 4px 0;
              color: #000;
            }
            .info-row strong {
              display: inline-block;
              width: 120px;
              color: #36348E;
            }
            .section-title {
              background-color: #CDD4FB;
              color: #36348E;
              padding: 5px 8px;
              font-size: 13px;
              font-weight: 600;
              margin: 5px 0 5px 0;
            }
            .marksheet table {
              width: 100%;
              border-collapse: collapse;
              margin-bottom: 5px;
              font-size: 12px;
              color: #000;
            }
            .three-column-table {
              table-layout: fixed;
            }
            .three-column-table th:first-child, 
            .three-column-table td:first-child {
              width: 65%;
            }
            .three-column-table th:nth-child(2), 
            .three-column-table td:nth-child(2),
            .three-column-table th:nth-child(3), 
            .three-column-table td:nth-child(3) {
              width: 17.5%;
            }
            .assignment-total-table td:first-child {
              width: 82.5%;
            }
            .assignment-total-table td:last-child {
              width: 17.5%;
            }
            .marksheet table, .marksheet th, .marksheet td {
              border: 1px solid #CDD4FB;
            }
            .marksheet th {
            //   background-color: #CDD4FB;
              padding: 5px;
              text-align: left;
              font-weight: bold;
              color: #36348E;
            }
            .marksheet td {
              padding: 5px 5px;
            }
            .center {
              text-align: center;
            }
            .grand-total {
            //   background-color: #ffc53a;
              font-weight: bold;
              color: #36348E;
            }
            .classification {
              margin: 10px 0;
            }
            .classification table td {
              padding: 5px 5px;
            }
            .signatures {
              display: flex;
              justify-content: space-between;
            //   margin-top: 20px;
            }
            .signature-block {
              text-align: center;
              width: 40%;
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: flex-end;
              min-height: 60px;
            }
            .signature-line {
              border-top: 1px solid #36348E;
              width: 100%;
              padding-top: 5px;
              color: #36348E;
              font-weight: bold;
            }
            .signature-image {
              max-width: 100px;
              max-height: 50px;
              object-fit: contain;
              margin-bottom: -5px;
            }
            .signature-image-1 {
              max-width: 180px;
              max-height: 80px;
              object-fit: contain;
              margin-bottom: -20px;
            }
            
          `}
                </style>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>
                    Close
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default MarkSheetModal;
